import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { OutlookTab } from '../models/IOutlookPanel.interface';

/**
 * Open/close and navigation state for the globally-mounted Outlook panel.
 *
 * Deliberately holds no HTTP and triggers no loading. Opening the panel must never, on its own,
 * reach Microsoft Graph: a Graph call can escalate to an interactive MSAL popup, and browsers block
 * popups that do not originate from a user gesture. Keeping fetches out of here means every path
 * that can prompt starts at a click. Data lives in OutlookDataService.
 *
 * Same shape as PhonebookSidebarService — BehaviorSubjects exposed as readonly streams, with sync
 * getters for the header, which binds `isOpen` directly in its template.
 */
@Injectable({ providedIn: 'root' })
export class OutlookPanelService {
  // ─── Panel open/close ───
  private openSubject = new BehaviorSubject<boolean>(false);
  readonly isOpen$: Observable<boolean> = this.openSubject.asObservable();

  // ─── Active tab ───
  private activeTabSubject = new BehaviorSubject<OutlookTab>('mail');
  readonly activeTab$: Observable<OutlookTab> = this.activeTabSubject.asObservable();

  // ─── Selected message (drives the reading pane and the panel's expanded width) ───
  private selectedMessageIdSubject = new BehaviorSubject<string | null>(null);
  readonly selectedMessageId$: Observable<string | null> = this.selectedMessageIdSubject.asObservable();

  get isOpen(): boolean {
    return this.openSubject.value;
  }

  get activeTab(): OutlookTab {
    return this.activeTabSubject.value;
  }

  get selectedMessageId(): string | null {
    return this.selectedMessageIdSubject.value;
  }

  /** Opens the panel, optionally jumping straight to a tab. */
  open(tab?: OutlookTab): void {
    if (tab) {
      this.activeTabSubject.next(tab);
    }
    this.openSubject.next(true);
  }

  /**
   * Closes the panel and drops the reading-pane selection, so reopening lands on the list rather
   * than on whatever message happened to be open a session ago. The active tab is kept — returning
   * to the tab you were last on is useful; returning to a stale message is not.
   */
  close(): void {
    this.openSubject.next(false);
    this.selectedMessageIdSubject.next(null);
  }

  toggle(): void {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  setTab(tab: OutlookTab): void {
    if (tab === this.activeTabSubject.value) {
      return;
    }
    this.activeTabSubject.next(tab);
    // The reading pane belongs to the mail tab; leaving it selected would keep the panel expanded
    // to 860px on tabs that have nothing to put there.
    this.selectedMessageIdSubject.next(null);
  }

  selectMessage(id: string | null): void {
    this.selectedMessageIdSubject.next(id);
  }
}
